import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StaffClassService, StaffClassDetail } from '../services/staff-class.service';
import { StaffSessionService } from '../services/staff-session.service';

declare var customToastr: any;

interface StaffClass {
  id: number | string;
  className: string;
  section: string;
  totalStudents: number;
  classTeacherName: string;
  classTeacherInitials: string;
  classTeacherImage: string;
  color: string;
  raw: StaffClassDetail;
}

const CARD_COLORS = ['blue', 'pink', 'green', 'yellow'];
const SEARCH_DEBOUNCE_MS = 500;

@Component({
  selector: 'app-staff-dashboard',
  templateUrl: './staff-dashboard.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-dashboard.component.css']
})
export class StaffDashboardComponent implements OnInit {
  loading = true;
  loadingMore = false;
  loadError = false;

  pageNumber = 1;
  maxPageNumber = 1;

  classes: StaffClass[] = [];
  readonly skeletonPlaceholders = Array.from({ length: 4 }, () => ({}));

  searchString = '';
  private searchDebounceTimer: any;
  private isFirstLoad = true;

  constructor(
    private router: Router,
    private staffClassService: StaffClassService,
    private staffSession: StaffSessionService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadClasses();
  }

  get visibleClasses(): StaffClass[] {
    return this.classes;
  }

  async loadClasses(page: number = 1) {
    const staffDetails = this.staffSession.getStaffDetails();
    if (!staffDetails) {
      this.loading = false;
      this.loadError = true;
      return;
    }

    if (page === 1) {
      this.loading = true;
    } else {
      this.loadingMore = true;
    }
    this.loadError = false;
    try {
      const response = await firstValueFrom(
        this.staffClassService.getClassDetails(staffDetails.staffId, page, this.searchString)
      );
      const responseDetails = response as any;
      if (responseDetails.status && responseDetails.body) {
        const classDetails: StaffClassDetail[] = responseDetails.body.classDetails || [];
        const mapped = classDetails.map((c, i) => this.mapClassDetail(c, this.classes.length + i));
        this.classes = page === 1 ? mapped : [...this.classes, ...mapped];
        this.maxPageNumber = responseDetails.body.maxPageNumber || 1;
        this.pageNumber = page;

        const schoolAndStaffInfoData = responseDetails.body.schoolAndStaffInfoData;
        if (schoolAndStaffInfoData && schoolAndStaffInfoData.length) {
          this.staffSession.setSchoolAndStaffInfo(schoolAndStaffInfoData[0]);
        }

        this.staffSession.setFeatureList(responseDetails.body.features || []);
        this.staffSession.setExtensionServices(responseDetails.body.schoolExtensionServicePermissions || []);
        this.staffSession.setAnyUnreadBroadcast(!!responseDetails.body.isAnyUnreadBroadcast);
      } else {
        this.loadError = true;
        const message = await firstValueFrom(this.translate.get('couldntLoadYourClassesPleaseTryAgain'));
        customToastr(message, 'error');
      }
    } catch (error) {
      this.loadError = true;
      const message = await firstValueFrom(this.translate.get('couldntLoadYourClassesPleaseTryAgain'));
      customToastr(message, 'error');
    } finally {
      this.loading = false;
      this.loadingMore = false;
      this.isFirstLoad = false;
    }
  }

  loadMore() {
    if (this.maxPageNumber > this.pageNumber && !this.loadingMore) {
      this.loadClasses(this.pageNumber + 1);
    }
  }

  onSearchInput(value: string) {
    this.searchString = value;
    if (this.isFirstLoad) { return; }
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => this.loadClasses(1), SEARCH_DEBOUNCE_MS);
  }

  private mapClassDetail(c: StaffClassDetail, index: number): StaffClass {
    const classTeacherName = c.classTeacherName || '';
    return {
      id: c.classId ?? index,
      className: c.classNumber || '',
      section: c.classDivision || '',
      totalStudents: c.studentCount || 0,
      classTeacherName,
      classTeacherInitials: this.staffSession.getInitials(classTeacherName),
      classTeacherImage: c.classTeacherImage || '',
      color: CARD_COLORS[index % CARD_COLORS.length],
      raw: c,
    };
  }

  async openClass(cls: StaffClass) {
    if (!cls.totalStudents) {
      const thisClassHasNoStudentsYetTranslation = await firstValueFrom(this.translate.get('thisClassHasNoStudentsYet'));
      customToastr(thisClassHasNoStudentsYetTranslation, 'error');
      return;
    }
    this.staffSession.setClassData(cls.raw);
    this.router.navigate(['/staff/class', cls.id]);
  }

  async openTile(cls: StaffClass, tileKey: string, event?: Event) {
    event?.stopPropagation();
    if (!cls.totalStudents) {
      const thisClassHasNoStudentsYetTranslation = await firstValueFrom(this.translate.get('thisClassHasNoStudentsYet'));
      customToastr(thisClassHasNoStudentsYetTranslation, 'error');
      return;
    }
    this.staffSession.setClassData(cls.raw);
    this.router.navigate(['/staff/class', cls.id, tileKey]);
  }

  gradeNumber(cls: StaffClass): string {
    const match = cls.className.match(/\d+/);
    return match ? match[0] : cls.className.charAt(0);
  }
}
